import { auth } from '../lib/firebase';

const EMAIL_SERVICE_URL = process.env.REACT_APP_EMAIL_SERVICE_URL || 'http://localhost:8080';

export interface BulkEmailResult {
  sent: string[];
  failed: string[];
}

/** Thrown when the e-mail service answered, but none of the e-mails went out. */
export class EmailDeliveryError extends Error {
  constructor(public failed: string[]) {
    super(`E-mail delivery failed for ${failed.length} recipient(s)`);
  }
}

async function post(path: string, body: Record<string, string>): Promise<any> {
  const user = auth.currentUser;
  if (!user) throw new Error('User not authenticated');
  const token = await user.getIdToken();

  const response = await fetch(`${EMAIL_SERVICE_URL}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(body),
  });

  let data: any = null;
  try {
    data = await response.json();
  } catch {
    /* non-JSON body */
  }
  if (!response.ok) {
    throw new Error(data?.error || `Email service responded with ${response.status}`);
  }
  return data;
}

/**
 * The bulk endpoints answer 200 even when SMTP rejects every message and list
 * the outcome in `results`, so the result has to be checked here.
 */
const toBulkResult = (data: any): BulkEmailResult => {
  const sent: string[] = data?.results?.success || [];
  const failed: string[] = data?.results?.failed || [];
  if (failed.length > 0 && sent.length === 0) {
    console.error('E-mail service could not deliver to:', failed);
    throw new EmailDeliveryError(failed);
  }
  if (failed.length > 0) console.warn('E-mail service could not deliver to:', failed);
  return { sent, failed };
};

export const emailService = {
  async sendNewReleaseNotification(projectId: string, releaseId: string): Promise<BulkEmailResult> {
    return toBulkResult(await post('/send-new-release-notification', { project_id: projectId, release_id: releaseId }));
  },

  async sendDownloadNotification(downloadId: string): Promise<BulkEmailResult> {
    return toBulkResult(await post('/send-download-notification', { download_id: downloadId }));
  },

  async sendMemberAddedNotification(memberId: string): Promise<void> {
    await post('/send-member-added-notification', { member_id: memberId });
  },
};
